import { useState } from "react";
import { whatsappUrl } from "../../../shared/whatsapp";
import { formatArs } from "../../../shared/money";
import type { OrderSummary } from "../../../shared/types";
import { useI18n } from "../../i18n/I18nProvider";
import { useCatalog } from "../../lib/CatalogProvider";

interface Transfer {
  holder?: string;
  taxId?: string;
  bank?: string;
  cbu?: string;
  alias?: string;
}
interface Wallet {
  asset: string;
  network: string;
  address: string;
}

const parse = <T,>(raw: string | undefined, fallback: T): T => {
  try {
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
};

function Copy({ value }: { value: string }) {
  const { t } = useI18n();
  const [done, setDone] = useState(false);
  const copy = () => {
    navigator.clipboard
      ?.writeText(value)
      .then(() => {
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      })
      .catch(() => {});
  };
  return (
    <button type="button" className="btn btn-link btn-sm" onClick={copy}>
      {done ? t("order.manual.copied") : t("order.manual.copy")}
    </button>
  );
}

/** Bank transfer / crypto order still unpaid: the details, the exact amount and where to send the receipt. */
export function ManualPayment({ order }: { order: OrderSummary }) {
  const { t, pick, locale } = useI18n();
  const { settings } = useCatalog();
  const get = (k: string) => (settings[k] ? pick(settings[k]) : undefined);
  const transfer = parse<Transfer>(get("payment_transfer"), {});
  const wallets = parse<{ wallets?: Wallet[] }>(get("payment_crypto"), {}).wallets ?? [];
  const discountPct = Number(get("manual_payment_discount_pct") || 5);
  const wa = whatsappUrl(get("whatsapp"), `Hola! Te mando el comprobante del pedido ${order.orderNumber}.`);
  const expired = order.expiresAt ? new Date(order.expiresAt).getTime() < Date.now() : false;
  const deadline = order.expiresAt ? new Date(order.expiresAt).toLocaleString(locale === "es" ? "es-AR" : "en-US", { dateStyle: "short", timeStyle: "short" }) : "";
  const amount =
    order.chargeAmountMinor == null
      ? ""
      : order.paymentProvider === "crypto"
        ? `${(order.chargeAmountMinor / 100).toFixed(2)} USDT`
        : formatArs(Math.round(order.chargeAmountMinor / 100), locale);

  const rows: [string, string | undefined, boolean][] =
    order.paymentProvider === "transfer"
      ? [
          [t("order.manual.amount"), amount, true],
          [t("order.manual.holder"), transfer.holder, false],
          ["CUIT/CUIL", transfer.taxId, false],
          [t("order.manual.bank"), transfer.bank, false],
          ["CBU/CVU", transfer.cbu, true],
          ["Alias", transfer.alias, true],
        ]
      : [[t("order.manual.amount"), amount, true], ...wallets.map((w): [string, string, boolean] => [`${w.asset} · ${t("order.manual.network")} ${w.network}`, w.address, true])];

  return (
    <section className="manual-payment" aria-labelledby="manual-title">
      <h2 id="manual-title">{t("order.manual.title")}</h2>
      {expired ? <p className="field-error">{t("order.manual.expired")}</p> : <p>{t("order.manual.intro", { deadline })}</p>}
      <dl className="manual-details">
        {rows
          .filter(([, value]) => value)
          .map(([label, value, copy]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd className="mono">
                {value} {copy && <Copy value={value!} />}
              </dd>
            </div>
          ))}
      </dl>
      {order.paymentProvider === "crypto" && <p className="field-error">{t("order.manual.networkWarn")}</p>}
      {order.discountMinor > 0 && (
        <p className="muted small">{order.paymentProvider === "transfer" ? t("order.manual.transferPrice") : t("order.manual.discount", { discount: discountPct })}</p>
      )}
      <p>{t(wa ? "order.manual.send" : "order.manual.sendEmail")}</p>
      {wa && (
        <a className="btn btn-primary" href={wa} target="_blank" rel="noopener noreferrer">
          {t("order.manual.whatsapp")}
        </a>
      )}
    </section>
  );
}
